/**
 * useSeriesThumbnails - Hook for generating series thumbnails
 *
 * Generates 60x60 thumbnails showing the middle slice of each series.
 * Used by the left sidebar for draggable series preview.
 *
 * Uses S3 mode (wadouri: scheme) for DICOM loading.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { StudyMetadataResponse } from "../utils/dicomMetadataLoader";
import { loadS3PreviewFrame, PixelDataArray } from "../utils/dicomPreviewFrame";
import { getAcquisitionGroup } from "../utils/hangingProtocolService";
import { DEFAULT_WINDOWING } from "../constants/windowPresets";

// Thumbnail size (compact - 60x60px for single column layout)
const THUMBNAIL_SIZE = 60;

export interface SeriesThumbnail {
  seriesId: string;
  seriesDescription: string | null;
  instanceCount: number;
  canvas: HTMLCanvasElement | null;
  loading: boolean;
  error?: string;
}

/**
 * Render pixel data to a canvas using the given window center/width. Callers
 * pass the standard soft-tissue window (DEFAULT_WINDOWING) so the sidebar
 * preview matches the viewer, which opens every study at soft-tissue. Pixel
 * data is whatever the wadouri loader hands back post-sign-correction (HU for
 * signed CT, raw intensity for unsigned modalities).
 */
function renderToCanvas(
  pixelData: PixelDataArray,
  rows: number,
  columns: number,
  windowCenter: number,
  windowWidth: number,
): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = THUMBNAIL_SIZE;
  canvas.height = THUMBNAIL_SIZE;
  const ctx = canvas.getContext("2d")!;

  // Create temp canvas for full resolution
  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = columns;
  tempCanvas.height = rows;
  const tempCtx = tempCanvas.getContext("2d")!;
  const imageData = tempCtx.createImageData(columns, rows);

  const lower = windowCenter - windowWidth / 2;
  const upper = windowCenter + windowWidth / 2;
  const range = upper - lower || 1;
  for (let j = 0; j < pixelData.length; j++) {
    const v = pixelData[j];
    const clamped = v < lower ? lower : v > upper ? upper : v;
    const normalized = ((clamped - lower) / range) * 255;

    imageData.data[j * 4] = normalized;
    imageData.data[j * 4 + 1] = normalized;
    imageData.data[j * 4 + 2] = normalized;
    imageData.data[j * 4 + 3] = 255;
  }

  tempCtx.putImageData(imageData, 0, 0);

  // Fill background
  ctx.fillStyle = "#1f2937";
  ctx.fillRect(0, 0, THUMBNAIL_SIZE, THUMBNAIL_SIZE);

  // Scale to thumbnail with aspect ratio
  const scale = Math.min(THUMBNAIL_SIZE / columns, THUMBNAIL_SIZE / rows);
  const scaledWidth = columns * scale;
  const scaledHeight = rows * scale;
  const offsetX = (THUMBNAIL_SIZE - scaledWidth) / 2;
  const offsetY = (THUMBNAIL_SIZE - scaledHeight) / 2;

  ctx.drawImage(tempCanvas, offsetX, offsetY, scaledWidth, scaledHeight);
  return canvas;
}

/**
 * Hook to generate thumbnails for all series in a study
 *
 * Uses S3 mode (wadouri: scheme) for DICOM loading.
 */
export function useSeriesThumbnails(studyMetadata: StudyMetadataResponse | undefined) {
  const [thumbnails, setThumbnails] = useState<SeriesThumbnail[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  const studyMetadataRef = useRef(studyMetadata);
  studyMetadataRef.current = studyMetadata;

  // Load (or reload) a single series thumbnail. Used by both the initial
  // parallel load and the per-thumbnail retry path.
  const loadOne = useCallback(async (seriesId: string, signal: AbortSignal) => {
    const meta = studyMetadataRef.current;
    if (!meta?.series || !meta.canonical_cdn_base_url) return;

    setThumbnails((prev) =>
      prev.map((t) => (t.seriesId === seriesId ? { ...t, loading: true, error: undefined } : t)),
    );

    try {
      const instances = meta.series[seriesId];
      if (!instances || instances.length === 0) {
        throw new Error("No instances in series");
      }

      // Instances arrive pre-sorted by z_position from backend (anatomical order)
      const middleIndex = Math.floor(instances.length / 2);
      const middleInstance = instances[middleIndex];

      const { pixelData, rows, columns } = await loadS3PreviewFrame(
        meta.canonical_cdn_base_url,
        middleInstance,
      );

      // Don't check abort signal here — if we've already loaded the data,
      // render it. StrictMode can abort after a successful load but before
      // render.
      //
      // Always render the sidebar preview at the standard (soft-tissue) window,
      // ignoring the DICOM-embedded W/L. This mirrors the viewer, which now
      // opens every study at soft-tissue (see DEFAULT_WINDOWING wiring in
      // HangingProtocolGrid / hangingProtocolAtoms), so a thumbnail always
      // matches how its series looks in the viewport — no dull/washed-out
      // previews from an odd scanner-baked window.
      const canvas = renderToCanvas(
        pixelData,
        rows,
        columns,
        DEFAULT_WINDOWING.center,
        DEFAULT_WINDOWING.width,
      );

      if (!signal.aborted) {
        setThumbnails((prev) =>
          prev.map((t) => (t.seriesId === seriesId ? { ...t, canvas, loading: false } : t)),
        );
      }
    } catch (e) {
      if (signal.aborted) return;
      if (import.meta.env.DEV) {
        console.warn("[useSeriesThumbnails] load failed", { seriesId, error: e });
      }
      const message = e instanceof Error ? e.message : String(e);
      setThumbnails((prev) =>
        prev.map((t) => (t.seriesId === seriesId ? { ...t, loading: false, error: message } : t)),
      );
    }
  }, []);

  // Manual retry — used by the SeriesThumbnail error state. Reuses the
  // current AbortController so a study switch mid-retry still cancels.
  const retry = useCallback(
    (seriesId: string) => {
      const ctrl = abortControllerRef.current;
      if (!ctrl) return;
      void loadOne(seriesId, ctrl.signal);
    },
    [loadOne],
  );

  // The study's identity for reloading: its series, not the object (a caller
  // may rebuild the same metadata on every render).
  const seriesKey = studyMetadata?.series ? Object.keys(studyMetadata.series).join("|") : null;
  useEffect(() => {
    const studyMetadata = studyMetadataRef.current;
    if (!studyMetadata?.series) {
      setThumbnails([]);
      return;
    }

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    // Order thumbnails by acquisition group so series from the same scan pass
    // sit together (radiologist's mental model: "all venous-phase series next
    // to each other"). The dialog blocks drops across groups; visible grouping
    // makes the boundary discoverable before the drop instead of after.
    // Group rank = min series_number in the group, so groups appear in scan
    // chronological order. Series with no acquisition_group fall to the end.
    const seriesIds = Object.keys(studyMetadata.series);
    const meta = studyMetadata.series_metadata;
    const groupRank = new Map<string, number>();
    if (meta) {
      for (const id of seriesIds) {
        const m = meta[id];
        if (!m) continue;
        const group = getAcquisitionGroup(m);
        if (group == null) continue;
        const prev = groupRank.get(group);
        if (prev == null || m.series_number < prev) groupRank.set(group, m.series_number);
      }
    }
    seriesIds.sort((a, b) => {
      const ma = meta?.[a];
      const mb = meta?.[b];
      const ga = ma ? getAcquisitionGroup(ma) : null;
      const gb = mb ? getAcquisitionGroup(mb) : null;
      if (ga !== gb) {
        if (ga == null) return 1;
        if (gb == null) return -1;
        const ra = groupRank.get(ga) ?? Number.POSITIVE_INFINITY;
        const rb = groupRank.get(gb) ?? Number.POSITIVE_INFINITY;
        if (ra !== rb) return ra - rb;
      }
      return (
        (ma?.series_number ?? Number.POSITIVE_INFINITY) -
        (mb?.series_number ?? Number.POSITIVE_INFINITY)
      );
    });
    setThumbnails(
      seriesIds.map((seriesId) => ({
        seriesId,
        seriesDescription: studyMetadata.series_metadata?.[seriesId]?.series_description || null,
        instanceCount: studyMetadata.series![seriesId].length,
        canvas: null,
        loading: true,
      })),
    );

    void Promise.all(seriesIds.map((seriesId) => loadOne(seriesId, signal)));

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [seriesKey, loadOne]);

  return { thumbnails, retry };
}
